/**
 * FlameChip primitive (Plan 02-13, Task 1).
 *
 * UI-SPEC §"<FlameChip days variant>" + handoff `chores.jsx:97-101` +
 * `mobile.jsx:118-121`. Two variants (#152: streak er lime i handoffen):
 *
 *   - default   lime-tonen (`bg-lime-bg text-lime`), flammen arver fargen —
 *               streak-chipen på vanlige flater.
 *   - emphasis  fylt lime (`bg-fab text-on-fab`), den ene høylytte fargen,
 *               for helteflaten i lommeboken.
 *
 * Plural handling: i18next default plural rules (`_one` / `_other` suffix)
 * over `streaks.daysOnRow.unit`. The project does NOT install i18next-icu —
 * see Plan 02-13 deviations for the rationale.
 *
 * Tabular numerals (`tnum`) on the digit so streaks 1 → 99 → 100+ don't jiggle.
 *
 * Størrelser (#154): `sm` (28 px, «3 dager på rad») er standarden. `md` er
 * streak-chipen i toppen på Hjem (36 px, 14 px tekst): der står bare «6 dager»
 * synlig, slik skissen viser, og «på rad» er med for skjermlesere.
 */
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Icon } from './Icon';
import { TONE_CLASS } from './Pill';

export type FlameChipVariant = 'default' | 'emphasis';
export type FlameChipSize = 'sm' | 'md';

interface FlameChipProps {
  days: number;
  variant?: FlameChipVariant;
  size?: FlameChipSize;
  className?: string;
}

const SIZE: Record<FlameChipSize, string> = {
  sm: 'h-7 gap-1 px-2.5 text-xs',
  md: 'h-9 gap-1.5 px-3 text-sm',
};

const SURFACE: Record<FlameChipVariant, string> = {
  default: TONE_CLASS.lime,
  emphasis: 'bg-fab text-on-fab',
};

export function FlameChip({
  days,
  variant = 'default',
  size = 'sm',
  className,
}: FlameChipProps): ReactNode {
  const { t } = useTranslation();
  return (
    <span
      data-testid="flame-chip"
      className={[
        'inline-flex shrink-0 items-center rounded-pill font-extrabold',
        SIZE[size],
        SURFACE[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span data-testid="flame-chip-flame" className="shrink-0" aria-hidden="true">
        <Icon.Flame size={size === 'md' ? 18 : 14} />
      </span>
      <span data-testid="flame-chip-days" className="tnum">
        {days}
      </span>
      {size === 'md' ? (
        <span>
          {t('streaks.daysShort.unit', { count: days })}
          <span className="sr-only">{t('streaks.daysShort.srSuffix')}</span>
        </span>
      ) : (
        <span>{t('streaks.daysOnRow.unit', { count: days })}</span>
      )}
    </span>
  );
}
